/**
 * useTenant Hook
 * Access tenant context with React Query for data fetching
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTenant as useTenantContext } from '../context/TenantContext';
import tenantService from '../services/tenantService';

// Re-export the context hook for direct access
export { useTenant } from '../context/TenantContext';

// Hook for fetching all tenants (admin)
export function useAllTenants() {
  return useQuery({
    queryKey: ['tenants'],
    queryFn: tenantService.getAllTenants,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// Hook for fetching a specific tenant
export function useTenantDetails(tenantId) {
  return useQuery({
    queryKey: ['tenant', tenantId],
    queryFn: () => tenantService.getTenant(tenantId),
    enabled: !!tenantId,
    staleTime: 5 * 60 * 1000,
  });
}

// Hook for tenant users
export function useTenantUsers(tenantId) {
  return useQuery({
    queryKey: ['tenant-users', tenantId],
    queryFn: () => tenantService.getTenantUsers(tenantId),
    enabled: !!tenantId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

// Hook for tenant usage stats
export function useTenantUsage(tenantId) {
  return useQuery({
    queryKey: ['tenant-usage', tenantId],
    queryFn: () => tenantService.getTenantUsage(tenantId),
    enabled: !!tenantId,
    staleTime: 1 * 60 * 1000, // 1 minute
    refetchInterval: 60 * 1000, // Refetch every minute
  });
}

// Hook for tenant billing
export function useTenantBilling(tenantId) {
  return useQuery({
    queryKey: ['tenant-billing', tenantId],
    queryFn: () => tenantService.getTenantBilling(tenantId),
    enabled: !!tenantId,
    staleTime: 5 * 60 * 1000,
  });
}

// Mutation for creating a tenant
export function useCreateTenant() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: tenantService.createTenant,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
    },
  });
}

// Mutation for updating a tenant
export function useUpdateTenant() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ tenantId, updates }) => tenantService.updateTenant(tenantId, updates),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
      queryClient.invalidateQueries({ queryKey: ['tenant', variables.tenantId] });
    },
  });
}

// Mutation for deleting a tenant
export function useDeleteTenant() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: tenantService.deleteTenant,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
    },
  });
}

export default useTenantContext;

